import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { LogoIcon } from "@/components/Logo";
import {
  ArrowLeft, ArrowRight, X, Loader2, Eye, EyeOff, CheckCircle, MoreHorizontal,
  Globe, MapPin, School, BookOpen, Lock, Lightbulb, Mail, Phone
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const INDIAN_FLAG = "\u{1F1EE}\u{1F1F3}";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isSignup, setIsSignup] = useState(searchParams.get("mode") === "signup");
  const [step, setStep] = useState(1);

  // Fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [grade, setGrade] = useState("10");
  const [board, setBoard] = useState("CBSE");
  const [state, setState] = useState("");
  const [school, setSchool] = useState("");
  const [stream, setStream] = useState("Science");
  const [states, setStates] = useState([]);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  // OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  // Forgot password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPass, setForgotNewPass] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotLoading, setForgotLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const needsStream = parseInt(grade) >= 11;

  useEffect(() => {
    axios.get(`${API}/auth/states`).then(r => setStates(r.data.states || [])).catch(() => {});
  }, []);

  const handleSendOtp = async () => {
    if (phone.length < 10) { toast.error("Enter a valid 10-digit number"); return; }
    setSendingOtp(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { phone });
      setOtpSent(true);
      toast.success("OTP sent to your phone!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    }
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    try {
      await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      setOtpVerified(true);
      toast.success("Phone verified!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    }
  };

  const canProceedStep1 = () => {
    if (!name.trim()) { toast.error("Enter your name"); return false; }
    if (!grade) { toast.error("Select your grade"); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Fill all required fields"); return; }
    setLoading(true);
    try {
      if (isSignup) {
        const payload = {
          name, email, password, grade: parseInt(grade),
          phone: phone, state: state || null,
          board, school: school || null,
          stream: needsStream ? stream : null
        };
        const res = await axios.post(`${API}/auth/register`, payload);
        login(res.data.token, res.data.user);
        toast.success("Welcome to AI Studdy Buddy!");
      } else {
        const res = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password: password });
        login(res.data.token, res.data.user);
        toast.success("Welcome back!");
      }
      navigate("/home");
    } catch (err) {
      if (!err.response || err.response.status >= 502) {
        toast.error("Server is starting up. Please wait 10 seconds and try again.", { duration: 6000 });
      } else if (err.response.status === 401) {
        toast.error("Invalid email or password. Please check and try again.");
      } else {
        toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      }
    }
    setLoading(false);
  };

  const filteredStates = states.filter(s =>
    s.toLowerCase().includes(stateSearch.toLowerCase())
  );

  // ====== LOGIN SCREEN ======
  if (!isSignup) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <button data-testid="auth-back-btn" onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
          <div className="bg-white rounded-3xl p-8 shadow-xl" data-testid="auth-card">
            <div className="flex items-center gap-3 mb-6">
              <LogoIcon className="w-11 h-11" />
              <span className="font-heading text-xl font-extrabold"><span className="text-orange-500">Ai Studdy</span> <span className="text-blue-500">Buddy</span></span>
            </div>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 mb-1">Welcome Back!</h1>
            <p className="text-sm text-slate-500 mb-6">Log in to continue learning</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Email</label>
                <Input data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="rounded-xl border-2 border-slate-200 focus:border-orange-500 bg-slate-50 h-12" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Password</label>
                <div className="relative">
                  <Input data-testid="auth-password-input" type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} className="rounded-xl border-2 border-slate-200 focus:border-orange-500 bg-slate-50 h-12 pr-10" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end mt-1" />
              <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full rounded-2xl font-bold h-12 bg-brand-orange hover:bg-brand-orange-dark text-white shadow-md transition-all active:scale-95 mt-2">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log In"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <button data-testid="auth-toggle-btn" onClick={() => { setIsSignup(true); setStep(1); }} className="text-sm text-slate-500">
                Don&apos;t have an account? <span className="font-semibold text-orange-500">Sign Up</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ====== SIGNUP SCREEN ======
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <button data-testid="auth-back-btn" onClick={() => step === 2 ? setStep(1) : navigate("/")} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-slate-800 hover:bg-slate-100">
            {step === 2 ? <ArrowLeft className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </button>
          <span className="font-heading font-bold text-slate-800 text-sm">
            <span className="text-orange-400">Ai</span>StuddyBuddy
          </span>
          <div className="w-9" />
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-lg mx-auto px-4 pt-3">
        <div className="flex gap-2 mb-2">
          <div className={`h-1 flex-1 rounded-full ${step >= 1 ? "bg-orange-500" : "bg-white/10"}`} />
          <div className={`h-1 flex-1 rounded-full ${step >= 2 ? "bg-orange-500" : "bg-white/10"}`} />
        </div>
        <p className="text-slate-400 text-xs">Step {step} of 2</p>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}
            className="max-w-lg mx-auto px-4 py-6">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-800 mb-2">
              Tell us about <span className="text-orange-400">yourself</span>
            </h1>
            <p className="text-slate-500 text-sm mb-6">We&apos;ll customize your learning experience</p>

            {/* Name */}
            <div className="mb-5">
              <label className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Name</label>
              <Input data-testid="auth-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required
                className="rounded-xl border-2 border-slate-200 bg-slate-50 h-12 text-slate-800 placeholder:text-slate-400 focus:border-orange-500" />
            </div>

            {/* Grade */}
            <div className="mb-5">
              <label className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
                <BookOpen className="w-3.5 h-3.5" /> Grade
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[5,6,7,8,9,10,11,12].map(g => (
                  <button key={g} data-testid={`auth-grade-${g}`} onClick={() => setGrade(String(g))}
                    className={`rounded-2xl p-2.5 text-center font-heading font-bold text-lg transition-all ${grade === String(g) ? "bg-orange-500 text-white shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Stream for 11-12 */}
            {needsStream && (
              <div className="mb-5">
                <label className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2 block">Stream</label>
                <div className="grid grid-cols-2 gap-2">
                  {["Science", "Commerce"].map(s => (
                    <button key={s} data-testid={`auth-stream-${s.toLowerCase()}`} onClick={() => setStream(s)}
                      className={`rounded-2xl p-3 text-center border-2 transition-all ${stream === s ? "bg-white border-orange-500 shadow-md" : "bg-white/90 border-transparent"}`}>
                      <p className="font-heading font-bold text-slate-800 text-sm">{s}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Board */}
            <div className="mb-5">
              <label className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2 block">Board</label>
              <div className="grid grid-cols-2 gap-2">
                {["CBSE", "ICSE"].map(b => (
                  <button key={b} data-testid={`auth-board-${b.toLowerCase()}`} onClick={() => setBoard(b)}
                    className={`rounded-2xl p-3 text-center border-2 transition-all ${board === b ? "bg-white border-orange-500 shadow-md" : "bg-white/90 border-transparent"}`}>
                    <p className="font-heading font-bold text-slate-800 text-sm">{b}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* State */}
            <div className="mb-5 relative">
              <label className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
                <MapPin className="w-3.5 h-3.5" /> State (Optional)
              </label>
              <button onClick={() => setShowStateDropdown(!showStateDropdown)}
                className="w-full text-left rounded-xl border-2 border-slate-200 bg-slate-50 h-12 px-4 text-slate-800 text-sm">
                {state || "Select your state"}
              </button>
              {showStateDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-xl border border-slate-200 shadow-2xl max-h-52 overflow-y-auto" style={{ backgroundColor: '#ffffff', zIndex: 9999 }}>
                  <input className="w-full px-4 py-2 border-b border-slate-200 text-slate-800 text-sm placeholder:text-slate-400 outline-none sticky top-0" style={{ backgroundColor: '#ffffff' }} placeholder="Search state..."
                    value={stateSearch} onChange={e => setStateSearch(e.target.value)} autoFocus />
                  {filteredStates.map(s => (
                    <button key={s} className="w-full text-left px-4 py-2.5 text-slate-700 hover:bg-slate-100 text-sm" onClick={() => { setState(s); setShowStateDropdown(false); setStateSearch(""); }}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* School */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
                <School className="w-3.5 h-3.5" /> School (Optional)
              </label>
              <Input value={school} onChange={e => setSchool(e.target.value)} placeholder="Your school name"
                className="rounded-xl border-2 border-slate-200 bg-slate-50 h-12 text-slate-800 placeholder:text-slate-400 focus:border-orange-500" />
            </div>

            <Button data-testid="auth-continue-btn" onClick={() => { if (canProceedStep1()) setStep(2); }}
              className="w-full rounded-2xl font-bold h-13 py-4 bg-brand-orange hover:bg-brand-orange-dark text-white text-base shadow-glow-orange transition-all active:scale-95">
              Continue <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <div className="mt-6 text-center">
              <button data-testid="auth-toggle-btn" onClick={() => { setIsSignup(false); setStep(1); }} className="text-sm text-slate-500">
                Already have an account? <span className="font-semibold text-orange-400">Log In</span>
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}
            className="max-w-lg mx-auto px-4 py-6">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-800 mb-2">
              Create your <span className="text-orange-400">account</span>
            </h1>
            <p className="text-slate-500 text-sm mb-6">Set up your login credentials</p>

            <div className="bg-white rounded-3xl p-6 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Email</label>
                  <Input data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                    className="rounded-xl border-2 border-slate-200 focus:border-orange-500 bg-slate-50 h-12" />
                </div>

                {/* Password */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Password</label>
                  <div className="relative">
                    <Input data-testid="auth-password-input" type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6}
                      className="rounded-xl border-2 border-slate-200 focus:border-orange-500 bg-slate-50 h-12 pr-10" />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
                  className="w-full rounded-2xl font-bold h-12 bg-brand-orange hover:bg-brand-orange-dark text-white shadow-md transition-all active:scale-95 mt-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
                </Button>
              </form>
            </div>

            <div className="mt-6 text-center">
              <button data-testid="auth-toggle-btn" onClick={() => { setIsSignup(false); setStep(1); }} className="text-sm text-slate-500">
                Already have an account? <span className="font-semibold text-orange-400">Log In</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
